import { useSelector } from 'react-redux';
import { matchPath, useLocation, useParams } from 'react-router';

import type { IClient } from '@klp/common';
import { Client } from '@klp/common';
import { generatePath, AppRoutes } from '@klp/routing';
import {
    clearStorage,
    getFromStorage,
    saveToStorage,
    ParamType,
} from '@klp/utils';

import { appSelectors } from './store';

export const useAppParams = () => {
    const location = useLocation();
    const params = useParams<Record<string, string | undefined>>();
    const { trigger } = useSelector(appSelectors);

    const getParam = <T>(prop: string): T | undefined => {
        void trigger;
        const data = getFromStorage<T>(prop);
        if (data !== undefined) return data;
        if (params[prop] !== undefined) return params[prop] as T;
        return undefined;
    };

    const storeParam = <T>(prop: string, value: T) => {
        if (value === undefined || value === null) {
            clearStorage(prop);
        } else {
            saveToStorage(prop, value);
        }
    };

    const clearParam = (prop: string) => {
        clearStorage(prop);
    };

    const matchRoute = (...fragments: Array<string>) => {
        const path = generatePath(...fragments);

        return !!matchPath(
            location.pathname.replace(AppRoutes.mainPage(), ''),
            { path: path.replace(AppRoutes.mainPage(), '') },
        );
    };

    return {
        get selectedClient() {
            const client = getParam<IClient>(ParamType.selectedClient);
            return client ? Client.parseObject(client) : undefined;
        },
        set selectedClient(value) {
            storeParam(ParamType.selectedClient, value);
        },

        get isAdmin() {
            return getParam(ParamType.isAdmin);
        },
        set isAdmin(value: boolean | undefined) {
            storeParam(ParamType.isAdmin, value);
        },

        get lang() {
            return getParam(ParamType.lang);
        },
        set lang(value: string | undefined) {
            storeParam(ParamType.lang, value);
        },

        get messageId() {
            return getParam(ParamType.messageId);
        },
        set messageId(value: string | undefined) {
            storeParam(ParamType.messageId, value);
        },

        get messageListFilter() {
            return getParam(ParamType.messageListFilter);
        },
        set messageListFilter(value: object | undefined) {
            storeParam(ParamType.messageListFilter, value);
        },

        get invoiceListFilter() {
            return getParam(ParamType.invoiceListFilter);
        },
        set invoiceListFilter(value: object | undefined) {
            storeParam(ParamType.invoiceListFilter, value);
        },

        get tab() {
            return getParam(ParamType.tab);
        },
        set tab(value: string | undefined) {
            storeParam(ParamType.tab, value);
        },

        get referrer() {
            return getParam<string>(ParamType.referrer) || '';
        },
        set referrer(value: string | undefined) {
            value
                ? storeParam(ParamType.referrer, value)
                : clearParam(ParamType.referrer);
        },

        get sakId() {
            return getParam(ParamType.sakId);
        },
        set sakId(value: string | undefined) {
            storeParam(ParamType.sakId, value);
        },

        get contractPath() {
            return getParam(ParamType.contractPath);
        },
        set contractPath(value: string | undefined) {
            storeParam(ParamType.contractPath, value);
        },

        get byggNummer() {
            return getParam(ParamType.byggNummer);
        },
        set byggNummer(value: string | undefined) {
            storeParam(ParamType.byggNummer, value);
        },

        get kundeNummer() {
            return getParam<string | undefined>(ParamType.kundeNummer);
        },
        set kundeNummer(value: string | undefined) {
            storeParam(ParamType.kundeNummer, value);
        },

        storeParam,
        matchRoute,
        clearParam,
    } as const;
};
